import { apiFetch } from "./client.ts";

export type LeadStatus = "new" | "assigned_to_tech" | "canceled";

export const LEAD_STATUSES: LeadStatus[] = ["new", "assigned_to_tech", "canceled"];

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: "New",
  assigned_to_tech: "Converted to Job",
  canceled: "Canceled",
};

export const LEAD_STATUS_COLORS: Record<LeadStatus, string> = {
  new: "bg-blue-100 text-blue-700",
  assigned_to_tech: "bg-green-100 text-green-700",
  canceled: "bg-slate-100 text-slate-500",
};

export interface Lead {
  id: string;
  account_id: string;
  site_id: string;
  source: string;
  name: string | null;
  phone_e164: string | null;
  email: string | null;
  address_text: string | null;
  service_type: string | null;
  property_type: string | null;
  scheduled_time: string | null;
  gate_operator_kind: string | null;
  job_details: string | null;
  service_call_fee_cents: number | null;
  asap: boolean | null;
  notes: string | null;
  status: LeadStatus;
  urgency: string | null;
  triage_score: number | null;
  triage_tags: unknown;
  assigned_to_user_id: string | null;
  client_id: string | null;
  utm: Record<string, string> | null;
  created_at: string;
  updated_at: string;
}

export interface LeadCreatePayload {
  site_id: string;
  source?: string;
  name?: string;
  phone_e164?: string;
  email?: string;
  address_text?: string;
  service_type?: string;
  property_type?: "residential" | "commercial";
  gate_operator_kind?: string;
  job_details?: string;
  scheduled_time?: string;
  service_call_fee_cents?: number;
  notes?: string;
  asap?: boolean;
}

export interface LeadUpdatePayload {
  name?: string;
  phone_e164?: string;
  email?: string;
  address_text?: string;
  service_type?: string;
  property_type?: string | null;
  scheduled_time?: string | null;
  gate_operator_kind?: string;
  job_details?: string;
  service_call_fee_cents?: number | null;
  asap?: boolean;
  notes?: string;
  status?: LeadStatus;
  urgency?: string | null;
  assigned_to_user_id?: string | null;
}

export interface LeadList {
  items: Lead[];
  total: number;
  limit: number;
  offset: number;
}

export async function listLeads(params: {
  site_id?: string;
  status?: LeadStatus;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<LeadList> {
  const usp = new URLSearchParams();
  if (params.site_id) usp.set("site_id", params.site_id);
  if (params.status) usp.set("status", params.status);
  if (params.q) usp.set("q", params.q);
  usp.set("limit", String(params.limit ?? 50));
  usp.set("offset", String(params.offset ?? 0));
  return apiFetch<LeadList>(`/leads?${usp}`);
}

export async function createLead(payload: LeadCreatePayload): Promise<Lead> {
  return apiFetch<Lead>("/leads", { method: "POST", body: JSON.stringify(payload) });
}

export async function getLead(id: string): Promise<Lead> {
  return apiFetch<Lead>(`/leads/${id}`);
}

export async function updateLead(id: string, patch: LeadUpdatePayload): Promise<Lead> {
  return apiFetch<Lead>(`/leads/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}
